import { useMemo } from 'react';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useAppState } from '../../../lib/hooks/use-app-state.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { plainTextToLexicalSync } from '../../../lib/rich-text/plain-text-to-lexical.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import type { TaskBulkFormValues } from './task-bulk-form-dialog.tsx';
import { TaskBulkFormDialog } from './task-bulk-form-dialog.tsx';
import type { TaskCreateDialogProps } from './task-create-dialog.tsx';

export const TaskBulkCreateDialog = DialogStateManager.create<TaskCreateDialogProps>(
    ({ links, onClose, description, assignedToId, source }: TaskCreateDialogProps) => {
        const { visible, remove } = useDialog();
        const { platoUser } = useAppState();
        const sendEvent = useAnalytics();
        const { t } = useLocale('tasks');
        const { toast } = useToast();
        const apiUtils = api.useUtils();

        const createTasksMutation = api.platoUser.command.tasks.postTasksActions.createBulk.useMutation({
            onSuccess({ taskIds }, { shared, batch }) {
                void apiUtils.platoUser.query.tasks.getManyTasks.getListWithFilters.invalidate();
                void apiUtils.platoUser.query.tasks.getManyTasks.getUpcomingCustomerTasks.invalidate();
                void apiUtils.platoUser.query.tasks.getManyTasks.count.invalidate();
                void apiUtils.platoUser.getManyCustomers.getListWithFilters.invalidate();
                void apiUtils.platoUser.getManyQuotesOrders.getWithFilters.invalidate();

                const assigneeIds = batch.flatMap((task) => task.assignedToId ?? []);
                const [firstTaskId] = taskIds;

                const sharedPayload = {
                    assignor_id: platoUser.id,
                    due_date: shared.dueDate?.toISOString() ?? '',
                    is_assigned_to_oneself: assigneeIds.includes(platoUser.id),
                    related_entity_type: shared.links?.[0]?.modelType,
                    related_entity_id: shared.links?.[0]?.modelId,
                    source,
                };

                if (taskIds.length === 1 && firstTaskId) {
                    sendEvent('task_created', {
                        ...sharedPayload,
                        task_id: firstTaskId,
                        assignee_id: assigneeIds[0],
                    });
                } else {
                    sendEvent('tasks_bulk_created', {
                        ...sharedPayload,
                        task_ids: taskIds,
                        assignee_ids: assigneeIds,
                        task_count: taskIds.length,
                    });
                }

                toast({
                    icon: 'success',
                    title: t('tasks__toast__created', { count: batch.length }),
                });

                remove();
                onClose?.();
            },
            onError(error) {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: error.message,
                });
            },
        });

        const handleClose = (): void => {
            remove();
            onClose?.();
        };

        const initialValues = useMemo<Partial<TaskBulkFormValues>>(() => {
            const values: Partial<TaskBulkFormValues> = {};
            if (links) {
                values.links = links;
            }
            if (assignedToId) {
                values.assignedToIds = [assignedToId];
            }
            if (description) {
                values.description = plainTextToLexicalSync(description) ?? undefined;
            }
            return values;
        }, [links, assignedToId, description]);

        return (
            <TaskBulkFormDialog
                open={visible}
                onClose={handleClose}
                onSubmit={({ assignedToIds, ...shared }: TaskBulkFormValues) => {
                    createTasksMutation.mutate({
                        shared,
                        batch: assignedToIds.map((id) => ({ assignedToId: id })),
                    });
                }}
                isSubmitting={createTasksMutation.isPending}
                title={t('tasks__form_create_title')}
                initialValues={initialValues}
            />
        );
    },
);
